import { z } from 'zod';
/**
 * ehr#83 - the practice's switches for the two features built on staff's connected Google /
 * Microsoft accounts: putting appointments on each clinician's own calendar (calendar sync) and
 * sending the practice's email from one connected mailbox. Stored as
 * organizations.calendarSyncSettings / mailSettings (the API's calendar-sync and mail modules
 * read them; null = off). Each also needs its server switch (CALENDAR_SYNC_ENABLED,
 * NOTIFICATIONS_EMAIL_ENABLED) and the provider consoles (ehr#82).
 *
 * Client details on a calendar or in an email are PHI: they need the practice's BAA
 * acknowledgment (the connected accounts are Google Workspace / Microsoft 365 under a BAA, never a
 * personal Gmail). The API's gates enforce it on every send; these schemas refuse saving an
 * option the gate would ignore.
 */
export const SYNC_WINDOW_DAYS = [7, 14, 30, 60, 90];
export const MAIL_PROVIDERS = ['google', 'microsoft'];
export const calendarSyncInputSchema = z
    .object({
    enabled: z.boolean(),
    includeClientDetail: z.boolean(),
    inviteClient: z.boolean(),
    baaAcknowledged: z.boolean(),
    windowDays: z.number().int().min(1).max(90),
    // ehr#436: the practice calendar ('' = none); older clients that don't send it keep none
    practiceUserId: z.string().max(64).default(''),
    practiceProvider: z.union([z.literal(''), z.enum(MAIL_PROVIDERS)]).default(''),
})
    .refine((s) => s.baaAcknowledged || (!s.includeClientDetail && !s.inviteClient), {
    message: 'Client details and client invites need the BAA acknowledgment.',
    path: ['baaAcknowledged'],
})
    .refine((s) => !s.practiceUserId === !s.practiceProvider, {
    message: 'Pick the practice calendar again.',
    path: ['practiceUserId'],
});
export const mailSettingsInputSchema = z
    .object({
    enabled: z.boolean(),
    senderUserId: z.string().max(64),
    senderProvider: z.union([z.literal(''), z.enum(MAIL_PROVIDERS)]),
    baaAcknowledged: z.boolean(),
})
    .refine((s) => !s.enabled || s.baaAcknowledged, {
    message: 'Sending needs the BAA acknowledgment.',
    path: ['baaAcknowledged'],
})
    .refine((s) => !s.enabled || (!!s.senderUserId && !!s.senderProvider), {
    message: 'Pick the mailbox that sends.',
    path: ['senderUserId'],
});
/** The picker's value for one sender, and back. */
export const senderKey = (s) => `${s.provider}:${s.userId}`;
export function parseSenderKey(key) {
    const i = key.indexOf(':');
    return i > 0
        ? { senderProvider: key.slice(0, i), senderUserId: key.slice(i + 1) }
        : { senderProvider: '', senderUserId: '' };
}
//# sourceMappingURL=syncSettings.js.map